export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

import type { Action, MyRole } from "./permissions";

export interface Project {
  id: string;
  name: string;
  owner: string;
  records_count: number;
  member_count: number;
  last_photo_at: string | null;
  my_roles: MyRole[];
  /**
   * Absolute URL of the project's logo, or `null` when none is set. Raster
   * only (PNG/JPEG); managed from the project settings logo section and used
   * on server-rendered reports. Present on both the list and detail payloads.
   */
  logo: string | null;
}

// The /projects/1.0/default-roles/ endpoint returns a plain list of role names.
// There is no separate role id until a ProjectRole row is created on project
// creation; role NAMES are the identifier used across the member-creation flow.
export type DefaultRole = string;

export type LinkType = "view" | "edit";

export interface InviteLink {
  id: string;
  link_type: LinkType;
  token: string;
  is_active: boolean;
  share_url: string;
  created_at: string | null;
  created_by: string | null;
}

export interface ProjectCreatePayload {
  name: string;
  tags: string[];
  members: { email: string; roles: string[] }[];
}

export interface ProjectValidatePayload {
  name: string;
}

export interface ProjectValidateResponse {
  is_valid: boolean;
  message?: string;
}

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar: string | null;
}

export interface ProjectUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

/**
 * A user who has created at least one record in a project. Returned by the
 * record-creators picker endpoint — like {@link ProjectUser} but with the
 * avatar URL so the creator filter can render avatar + name.
 */
export interface RecordCreator {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
}

export interface ProjectTag {
  id: string;
  name: string;
  record_count: number;
}

export interface ProjectRole {
  id: string;
  name: string;
  /**
   * The actions the role holds. Sent on the project detail's own `roles`
   * only — a member's `roles` are id and name — so each role's actions travel
   * once rather than once per member. Absent on older backends. Read a
   * member's level through `memberAccess`, never by the role's name.
   */
  actions?: Action[];
  /**
   * A role the server defines and locks, such as Contributor: it can't be
   * edited, renamed or deleted. Sent alongside `actions`.
   */
  is_system?: boolean;
}

export interface ProjectMember {
  id: string;
  user: ProjectUser;
  roles: ProjectRole[];
}

/** Server-side tile pipeline state. `null` = legacy non-tiled image. */
export type TileStatus = "pending" | "processing" | "ready" | "failed";

/**
 * Inline DZI tile-source metadata (no XML manifest — the serializer ships
 * this JSON directly). Tile URL = `${base_url}/${level}/${x}_${y}.${format}`.
 */
export interface TilesMeta {
  base_url: string;
  width: number;
  height: number;
  tile_size: number;
  overlap: number;
  format: string;
}

export interface ProjectPlanRevision {
  id: string;
  plan: string;
  name: string;
  image_url: string | null;
  thumbnail_url: string | null;
  created_by: string | null;
  created_at: string;
  /**
   * Optional — absent on payloads cached before the tiles deploy. Treat
   * `undefined` and `null` identically (legacy image). Fixture rule: copy
   * the field list from the backend serializer, not from this type
   * (see memory: v1.2 serializer false-positive tests).
   */
  tile_status?: TileStatus | null;
  tiles?: TilesMeta | null;
  tile_error?: string;
}

export interface ProjectPlan {
  id: string;
  name: string;
  latest_revision: ProjectPlanRevision | null;
  revision_count: number;
  record_count: number;
  deleted_at: string | null;
}

export interface ProjectMetadataField {
  id: string;
  name: string;
  field_type: "text" | "number" | "date" | "single_select" | "multi_select" | "boolean";
  options?: unknown;
  required: boolean;
  default_value: unknown;
  help_text: string;
  sort_order: number;
}

export interface ProjectImage {
  id: string;
  image: string;
  thumbnail: string | null;
  taken_at: string | null;
  /**
   * Optional v1.2 extensions. `original_url` is the untouched upload bytes
   * (may be null for legacy rows predating the v1.2 column), used by the
   * Phase 2 re-edit flow to rehydrate the markup editor. Only present when
   * the record was fetched from a v1.2 endpoint.
   */
  original_url?: string | null;
  annotated_url?: string | null;
  thumbnail_url?: string | null;
  report_image_url?: string | null;
  /**
   * Per-photo client idempotency key. Echoed by the v2.0 read serializer
   * (`ProjectImageV2_0Serializer`) so the append outbox can reconcile an
   * optimistic tile to its synced server twin once an append lands — see
   * [[append-photo-outbox]]. Absent on v1.x reads.
   */
  client_photo_uuid?: string | null;
  /**
   * Per-photo markup annotations (`Drawing[]` + crop + rotation + downscale),
   * stored opaquely by the server and echoed by v2.0 reads
   * (`ProjectImageV2_0Serializer`). Seeds the per-photo markup re-edit.
   * Absent on v1.x reads.
   */
  annotations?: RecordAnnotations | null;
}

/**
 * v1.2 annotations payload — mirrors the `Drawing[]` + crop + downscale
 * schema produced by the image markup editor. Surfaced at the top level of
 * the record response by v1.2 endpoints. Shape kept permissive here so
 * `types/api.ts` doesn't import markup-editor internals.
 */
export interface RecordAnnotations {
  drawings?: unknown[];
  crop?: { x: number; y: number; width: number; height: number } | null;
  /**
   * Clockwise quarter-turns applied to the source photo before crop and
   * drawings. Absent / null means 0 — every record written before rotation
   * shipped. `drawings` and `crop` are stored in the space this produces, so
   * the re-edit path must feed it back to the editor.
   */
  rotation?: number | null;
  downscaleMaxPx?: number | null;
  date_stamp?: unknown;
}

export interface ProjectRecord {
  id: string;
  /**
   * The record's cover / legacy single photo. **Optional** as of the
   * multi-photo v2.0 read: that endpoint returns the full photo set as
   * top-level `images[]` and carries **no** top-level `image`. Today's
   * v1.0/v1.1/v1.2 reads (and the share adapter) still populate it. Never read
   * this field directly — resolve the cover via `coverImage()` and the full
   * set via `recordImages()` (`@/lib/record-images`), which fall back across
   * both shapes and stay correct whichever the server sent.
   */
  image?: ProjectImage;
  /**
   * Multi-photo set, ordered by taken_at/created_at. Present from the v2.0
   * read; `undefined` on the v1.0/v1.1/v1.2 responses. Prefer
   * `recordImages(record)`, which falls back to `[image]`.
   */
  images?: ProjectImage[];
  plan: string | null;
  pin_x: number | null;
  pin_y: number | null;
  /**
   * Plan-relative camera bearing in degrees `[0, 360)`, clockwise, 0° =
   * drawing-top. Optional and nullable — `null` (the default, and every
   * pre-existing pin) means no direction. A bearing requires a pin. See
   * [[directional-pins]].
   */
  pin_bearing?: number | null;
  tags: string[];
  metadata: Record<string, unknown>;
  created_by: ProjectUser;
  created_at: string | null;
  updated_at: string;
  /** v1.2-only: top-level annotation payload mirrored from `image.annotations`. */
  annotations?: RecordAnnotations | null;
}

/**
 * Issue lifecycle. Four values, but only three are ever *chosen*: `assigned` is
 * written once, when the issue is raised, and is never the target of an update.
 *
 * These are wire values — the enum the server stores, the `status` filter it
 * accepts, and the keys of {@link IssueSummary}. They are not display labels:
 * `assigned` renders as **Pending** everywhere, because the status is about
 * the lifecycle rather than the assignee (`assigned_to` may well be null).
 * `issueStatusLabel` owns that mapping. See [[issues.spec]].
 */
export type IssueStatus =
  | "assigned"
  | "in_progress"
  | "completed"
  | "closed";

/** The statuses a user may pick in an update, in lifecycle order. */
export const SELECTABLE_ISSUE_STATUSES = [
  "in_progress",
  "completed",
  "closed",
] as const satisfies readonly IssueStatus[];

export type SelectableIssueStatus =
  (typeof SELECTABLE_ISSUE_STATUSES)[number];

/**
 * What a trail row records.
 *
 * `update` is someone acting on the issue; `edit` is someone amending its
 * header. Both are the same row shape and both carry a status — an edit row
 * carries the status the issue already stood at, so the trail stays a complete
 * state log with no "this one changed nothing" branch in it. See
 * [[issues.spec]] § Editing an issue.
 */
export type IssueUpdateKind = "update" | "edit";

/**
 * One field that moved in an edit.
 *
 * `old` and `new` are **server-rendered display strings**, always — names, not
 * ids; `"15 Aug 2026"`, not an ISO date. The trail is history and must render
 * identically forever, so an assignee later removed from the project still
 * reads by the name they had at the time and the client never needs a second
 * request to resolve an id. Nulls arrive as `"Unassigned"` / `"No due date"`,
 * never as an empty string.
 *
 * `field` is the backend's model field name and is deliberately typed as a
 * plain string rather than a union: the server owns that vocabulary, and a
 * change this client cannot name must still render. See
 * [[issue-activity-trail.spec]].
 */
export interface IssueFieldChange {
  field: string;
  old: string;
  new: string;
}

/**
 * One append-only event on an issue. Never edited, never deleted.
 *
 * `status` is always present, even when it matches the status the issue
 * already had — that is what makes the trail a complete state log rather than
 * a log with holes in it. See [[issues.spec]].
 */
export interface IssueUpdate {
  id: string;
  /** Null when the author's account is gone; renders as "Unknown user". */
  author: ProjectUser | null;
  remarks: string;
  status: IssueStatus;
  /**
   * What this update forecast — an ISO date (`yyyy-MM-dd`), no time — or null
   * when it said nothing about one.
   *
   * Not the issue's deadline: `due_date` is what was asked for and this is what
   * came back from a chase, and the gap between them is the whole subject. The
   * issue itself carries no target; the current one is the newest update
   * holding a non-null date, so a later update with none says nothing rather
   * than clearing it. See [[issues.spec]] § Data contract → Update.
   */
  target_date: string | null;
  /**
   * Photos of the work done. These belong to the update, not to the issue's
   * record — they never appear in the Photos tab. Same shape as record photos
   * so the shared viewer renders them unchanged; `original_url` is null on
   * every one, because issue evidence is never annotated.
   */
  images: ProjectImage[];
  client_update_uuid: string | null;
  created_at: string;
  /** Server-defaulted to `update`, so every pre-existing row reads as one. */
  kind: IssueUpdateKind;
  /** Empty on an `update` row; one entry per field that moved on an `edit`. */
  changes: IssueFieldChange[];
}

/**
 * An actionable item raised against a record.
 *
 * `assigned_to` and `created_by` arrive **expanded**, not as ids: the list
 * renders names on every row and must not need a second request to do it.
 * Writes go the other way, through `assigned_to_id`.
 *
 * `status` is read-only. The only thing that moves it is posting an update —
 * there is no endpoint that sets it directly, by design.
 */
export interface ProjectIssue {
  id: string;
  /**
   * Per-project sequential display number, allocated server-side. Rendered
   * zero-padded to three digits (`001`).
   */
  number: number;
  /** Id of the record whose photos show the problem. Never null. */
  record: string;
  title: string;
  description: string;
  /** Null is a real state — the UI offers a Reassign action for it. */
  assigned_to: ProjectUser | null;
  /** The raiser. Load-bearing: identity alone grants the right to close. */
  created_by: ProjectUser | null;
  /**
   * Whether the viewer is involved in this issue: raised it, is assigned to
   * it, or was invited to it. Only the server knows about invitations; a
   * backend that predates them leaves this out, and raiser or assignee is then
   * the whole rule. Read it through `isInvolvedInIssue`, never directly.
   */
  viewer_involved?: boolean;
  status: IssueStatus;
  /**
   * Absolute URL of the cover photo of the issue's record, for the list row
   * thumbnail. Null when the record has no photos yet.
   *
   * Only the cover is exposed. A screen that needs the whole photo set (the
   * detail page's evidence strip) reads the record itself — one request on a
   * detail screen rather than N on a list.
   */
  record_cover_thumbnail: string | null;
  /** ISO date (no time). Null when the issue has no deadline. */
  due_date: string | null;
  /**
   * The newest non-null forecast from the update trail. Present on list rows;
   * detail reads derive the same fact from `updates`.
   */
  target_date?: string | null;
  client_issue_uuid: string | null;
  created_at: string | null;
  updated_at: string;
  /**
   * The full activity trail. Present on the **detail** read only — the list
   * shape omits it, because a page of issues times their whole history is not
   * a payload to send over a jobsite connection.
   */
  updates?: IssueUpdate[];
  /**
   * Detail only: everyone invited onto the issue, oldest first. Absent on list
   * rows and on backends that predate contributors — read absent as "unknown",
   * never as "nobody".
   */
  contributors?: IssueContributor[];
  /**
   * Detail only: whether the viewer may bring people onto the issue — anyone
   * who can post on it, while it isn't closed. Puts Add contributor in the
   * action bar. See [[issue-detail-page.spec]].
   */
  viewer_can_invite?: boolean;
  /**
   * Detail only: whether the viewer may also pick someone from the project's
   * member list, rather than only sending a link. Needs `issue:View:All`.
   */
  viewer_can_add_from_project?: boolean;
}

/**
 * Someone invited onto an issue, through a link or from the project's member
 * list. The raiser and the assignee are on the issue by raising or holding it
 * and never appear here. Active rows only: a removed contributor is gone from
 * the list. See [[issue-contributors.spec]].
 */
export interface IssueContributor {
  id: string;
  user: ProjectUser;
  /** Null when the inviter's account is gone. */
  invited_by: ProjectUser | null;
  joined_at: string;
  /**
   * Whether the viewer may take this person off the issue: anyone with
   * `issue:Edit`, or whoever invited them. The server's answer; the client
   * never works it out.
   */
  can_remove: boolean;
}

/**
 * A fresh link onto an issue. It lets new people in for 7 days, while the
 * issue is open and its maker can still add people. Nobody lists or manages
 * links. See [[add-contributor-sheet.spec]].
 */
export interface IssueInviteLink {
  token: string;
  /** The web app's invite page for this link — what gets shared. */
  url: string;
  expires_at: string;
  created_at: string;
}

/**
 * What an issue shows of its record: the photos, the pin and the plan it sits
 * on. Readable by anyone who can see the issue, whether or not they may view
 * records, so nothing else of the record is here. `created_at` dates a photo
 * with no capture time. See [[issues.spec]] § The record behind an issue.
 */
export interface IssueRecord
  extends Pick<
    ProjectRecord,
    "id" | "plan" | "pin_x" | "pin_y" | "pin_bearing" | "created_at"
  > {
  /** Ordered by capture time, so the first is the cover. */
  images: ProjectImage[];
}

/**
 * What an issue link's page may show before sign-in: who sent it and for which
 * project. Nothing from the issue. See [[issue-invite-page.spec]].
 */
export interface IssueInvitePreview {
  project_name: string;
  /** Null when the sender's account has no name on file. */
  inviter_name: string | null;
}

/** Where a joined issue link leads. */
export interface IssueInviteJoin {
  project_id: string;
  issue_id: string;
}

/** Per-status counts behind the issues list header. Every status is present. */
export type IssueSummary = Record<IssueStatus, number>;

export type ReportStatus = "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";

export type ReportFormat = "pdf" | "pptx";

export interface ProjectReport {
  id: string;
  project: string;
  created_by: string;
  file: string | null;
  name: string;
  thumbnail: string | null;
  status: ReportStatus;
  /**
   * Output format of the generated file. Backend non-nullable with a `'pdf'`
   * default, so legacy reports (created before this column shipped) read as
   * `'pdf'` — which matches reality: every report was a PDF back then. Drives
   * how the row opens the file — PDFs open inline in a new tab; every other
   * format downloads in-app and hands off to the OS share/preview sheet
   * (see report-item.spec).
   */
  format: ReportFormat;
  error_message: string;
  /**
   * Backend nullable: legacy reports created before this column shipped
   * have null. New reports are populated at generation time with the
   * `records` array length. `useReportProgress` treats null/0/negative
   * the same way (falls back to a 60-second default duration).
   */
  record_count: number | null;
  completed_at: string | null;
  created_at: string | null;
  updated_at: string;
}

/** What a collection holds. Fixed when it is created. */
export type CollectionKind = "records" | "issues";

/**
 * How much of each issue's update trail an export carries. Null on a records
 * export — only issues have a trail. The client never sends it: every issues
 * export shows each issue's latest status (decision 2026-09-29, see
 * [[report-collections.spec]]).
 */
export type ExportHistoryScope = "all" | "since_last_export" | "latest_only";

/**
 * One dated file made from a {@link ReportCollection} — a *version*, in the
 * words the UI uses.
 *
 * Every display field is frozen at generation rather than read back off the
 * collection, because the collection may have changed since. That is what
 * makes editing a collection safe: it cannot rewrite the history of a file
 * that has already been sent.
 */
export interface CollectionExport {
  id: string;
  collection: string;
  created_by: ProjectUser | null;
  /** Frozen at publish. Renaming the collection does not touch it. */
  name: string;
  status: ReportStatus;
  template: string;
  format: ReportFormat;
  orientation: string | null;
  history_scope: ExportHistoryScope | null;
  file: string | null;
  thumbnail: string | null;
  /** Frozen. Never recomputed from the collection. */
  item_count: number;
  error_message: string;
  completed_at: string | null;
  created_at: string | null;
  updated_at: string;
}

/**
 * A durable, hand-picked set of photos or issues that reports are exported
 * from. Called a **Report** everywhere the user can see it; "collection" is
 * the wire and spec word only. See [[report-collections.spec]].
 */
export interface ReportCollection {
  id: string;
  project: string;
  kind: CollectionKind;
  name: string;
  created_by: ProjectUser | null;
  /** Live entries. Excludes tombstones. */
  item_count: number;
  /**
   * Photos across the live entries; null on an issues collection. This is the
   * number a records collection shows, because one entry may hold several
   * photos and the label says Photos.
   */
  photo_count: number | null;
  /** Entries whose photo or issue has been deleted from the project. */
  deleted_item_count: number;
  template: string;
  format: ReportFormat;
  orientation: string | null;
  /**
   * The newest export. Never null in practice — a collection is created by
   * exporting — but typed nullable so a partial cache entry cannot crash a row.
   */
  latest_export: CollectionExport | null;
  created_at: string | null;
  updated_at: string;
}

/**
 * One membership row.
 *
 * `record` and `issue` are both null once the thing the row points at has been
 * deleted from the project; the row itself survives so the collection can show
 * a tombstone rather than quietly coming back one item shorter. `is_deleted`
 * also covers a soft-deleted issue, where the FK is intact but the issue is
 * hidden — read the flag, not the fields.
 */
export interface ReportCollectionItem {
  id: string;
  record: ProjectRecord | null;
  issue: ProjectIssue | null;
  is_deleted: boolean;
  created_at: string | null;
}

export interface ProjectDetail extends Omit<Project, "owner"> {
  tags: ProjectTag[];
  members: ProjectMember[];
  roles: ProjectRole[];
  plans: ProjectPlan[];
  metadata_fields: ProjectMetadataField[];
  max_record_count: number;
  /** Full user object (detail endpoint returns expanded owner, not just ID) */
  owner: ProjectUser;
}

export type AutodeskPushStatus =
  | "pending"
  | "creating"
  | "attaching"
  | "completed"
  | "failed";

/**
 * A record's push row — mirrors `RecordAutodeskPushSerializer` field-for-field
 * (`integrations/autodesk/serializers.py`). Returned by both the POST and GET
 * `.../records/{recordId}/autodesk-push/` endpoints.
 */
export interface RecordAutodeskPush {
  id: string;
  status: AutodeskPushStatus;
  forma_issue_id: string | null;
  forma_display_id: number | null;
  issue_url: string | null;
  photos_total: number;
  photos_attached: number;
  error_detail: { type: string; message: string } | null;
  pushed_at: string | null;
}

/**
 * A project's Autodesk connection config, read shape — mirrors
 * `ProjectAutodeskConnectionSerializer`. Does not include `field_config` —
 * the backend accepts it on write, but it is not (yet) exposed on
 * {@link AutodeskConnectionPayload}, so the frontend has no way to send it
 * either. Revisit both types together if/when the panel needs to configure
 * it.
 */
export interface AutodeskConnection {
  id: string;
  forma_hub_id: string;
  forma_project_id: string;
  /**
   * Human-readable Forma project name, resolved server-side from
   * `forma_project_id`. The settings panel shows this as the connected
   * project's primary label; it falls back to rendering the raw
   * `forma_project_id` when this is blank (an older row, or a name the
   * backend couldn't resolve), so a blank name is never shown to the user.
   */
  forma_project_name: string;
  default_subtype_id: string | null;
  default_assignee_id: string | null;
  is_active: boolean;
}

/**
 * Write payload for `PUT .../autodesk-connection/`. The endpoint behaves
 * PATCH-like server-side: an OMITTED optional field is preserved on update,
 * an EXPLICIT `null` clears it. `default_subtype_id`/`default_assignee_id`
 * are optional for exactly this reason — the project-settings panel omits
 * them on every save so it never clobbers a subtype/assignee configured
 * some other way (e.g. ops-set, or a future connect-time auto-resolve);
 * callers that DO want to clear one must send `null` for it explicitly.
 */
export interface AutodeskConnectionPayload {
  forma_hub_id: string;
  forma_project_id: string;
  default_subtype_id?: string | null;
  default_assignee_id?: string | null;
  is_active?: boolean;
}

/**
 * The org-level Autodesk robot identity, read shape — mirrors the
 * `GET .../autodesk-robot/` response. `email` is the address a customer's
 * Autodesk admin must invite to their Forma projects before the robot can
 * see (and push to) anything; the settings panel surfaces it in the
 * zero-projects health-check message. The endpoint's 404 ("integration not
 * enabled for your organization" — no robot minted) is not represented
 * here: `useAutodeskRobot` resolves it to `null`, mirroring
 * {@link AutodeskConnection}'s 404 handling.
 */
export interface AutodeskRobot {
  email: string;
  is_active: boolean;
}

export interface FormaProject {
  hub_id: string;
  project_id: string;
  name: string;
}

export interface FormaProjectListResponse {
  projects: FormaProject[];
  count: number;
}
